<template>
    <v-dialog v-model="show" fullscreen transition="slide-y-transition" overlay-color="#111" overlay-opacity="0.95"
        :style="`${isAndroidNative ? 'margin-top: 100px; height: 100vh;' : ''} `">
        <v-card class="bg-dark pa-4 ticket-dialog">
            <v-btn variant="tonal" class="mt-6" @click="close"
                style="position: absolute; top: 12px; right: 12px; z-index: 10; transition: 0.3s ease;color: #fff;">

                {{ $t('closeButton') }}
            </v-btn>
            <div class="content-scroll">
                <h2 class="text-white mb-6 text-center font-weight-bold">
                    {{ $t('operations.title') }}
                </h2>

                <!-- Ticket Categories -->
                <v-row class="mb-6" justify="center" align="center" no-gutters>
                    <v-col v-for="issue in $tm('operations.issues')" :key="issue.title" cols="12" sm="6" md="4"
                        class="pa-2">
                        <v-card class="ticket-card">
                            <v-icon :color="issue.color" size="32">{{ issue.icon }}</v-icon>
                            <div class="text-white font-weight-bold mt-2">{{ issue.title }}</div>
                            <ul class="text-white mt-2 bullet-list">
                                <li v-for="point in issue.points" :key="point">{{ point }}</li>
                            </ul>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Admin Tools -->
                <v-row justify="center" class="mt-2" no-gutters>
                    <v-col v-for="tool in $tm('operations.tools')" :key="tool.label" cols="12" sm="6" md="4"
                        class="pa-2">
                        <v-card class="ticket-card">
                            <v-icon :color="tool.color" size="32">{{ tool.icon }}</v-icon>
                            <div class="text-white font-weight-bold mt-2">{{ tool.label }}</div>
                            <ul class="text-white mt-2 bullet-list">
                                <li v-for="desc in tool.points" :key="desc">{{ desc }}</li>
                            </ul>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, defineExpose } from 'vue';
import { Capacitor } from '@capacitor/core';

const isAndroidNative = Capacitor.getPlatform() === 'android' && Capacitor.isNativePlatform();

const show = ref(false);
function open() {
    show.value = true;
}
function close() {
    show.value = false;
}
defineExpose({ open });

const cards = [
    {
        title: 'Login Issues',
        icon: 'mdi-login-variant',
        color: 'blue lighten-2',
        points: ['SSO failures', 'Invalid credentials', 'Timeouts or redirects']
    },
    {
        title: 'App Performance',
        icon: 'mdi-speedometer',
        color: 'deep-orange lighten-1',
        points: ['Slow loading screens', 'Unexpected crashes', 'Animations lagging']
    },
    {
        title: 'Notification Problems',
        icon: 'mdi-bell-alert',
        color: 'amber darken-2',
        points: ['Push not received', 'Repeated alerts', 'Silent mode conflicts']
    },
    {
        title: 'Data Sync Issues',
        icon: 'mdi-cloud-sync',
        color: 'cyan',
        points: ['Real-time data missing', 'Outdated dashboard info', 'Sync conflicts']
    },
    {
        title: 'Device Compatibility',
        icon: 'mdi-devices',
        color: 'lime darken-1',
        points: ['Mobile vs desktop bugs', 'Browser limitations', 'Tablet UI misalignment']
    },
    {
        title: 'Integration Failures',
        icon: 'mdi-puzzle',
        color: 'purple lighten-1',
        points: ['3rd party system errors', 'Calendar/lock/IoT issues', 'API unresponsive']
    }
];

const tools = [
    {
        icon: 'mdi-clock-outline',
        label: 'SLA Tracking',
        color: 'cyan',
        points: ['Track SLA deadlines', 'Escalate overdue tickets']
    },
    {
        icon: 'mdi-eye-outline',
        label: 'Status Transparency',
        color: 'light-blue',
        points: ['Monitor ticket states', 'Access resolution logs']
    },
    {
        icon: 'mdi-shield-cog-outline',
        label: 'Admin Control',
        color: 'green lighten-2',
        points: ['Assign support roles', 'Manage permissions']
    }
];
</script>

<style scoped>
.bg-dark {
    background-color: #111;
    min-height: 100vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.ticket-dialog {
    width: 100%;
    max-width: 100vw;
    margin: 0 auto;
}

.close-btn {
    position: fixed;
    top: 12px;
    right: 12px;
    z-index: 100;
    background-color: #000000cc;
}

.content-scroll {
    width: 100%;
    max-width: 1100px;
    padding: 16px;
    padding-top: 64px;
    box-sizing: border-box;
}

.ticket-card {
    background-color: #1e1e1e;
    padding: 20px;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 255, 255, 0.06);
    text-align: center;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    min-height: 200px;
}

.ticket-card:hover {
    transform: scale(1.03);
    box-shadow: 0 8px 20px rgba(0, 255, 255, 0.12);
}

.bullet-list {
    text-align: left;
    font-size: 0.9rem;
    margin-top: 8px;
    padding-left: 16px;
    list-style: disc;
}

::-webkit-scrollbar {
    width: 4px;
}

::-webkit-scrollbar-track {
    background: transparent;
}

::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 255, 0.15);
    border-radius: 8px;
}

::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 255, 255, 0.3);
}

* {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 255, 255, 0.15) transparent;
}
</style>